import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CalendarCheck, Compass, Code2, Rocket, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import PillButton from "./PillButton";

const STEPS = [
    {
        n: "01",
        icon: CalendarCheck,
        title: "Audit gratuit",
        desc: "On regarde votre situation actuelle, ce qui marche déjà, et ce qui vous fait perdre des clients. 30 minutes en visio, sans engagement.",
        cta: true,
    },
    {
        n: "02",
        icon: Compass,
        title: "Stratégie & maquette",
        desc: "On conçoit le site qui convient à VOTRE métier. Pas un template. Chaque page est pensée pour déclencher une demande de devis.",
    },
    {
        n: "03",
        icon: Code2,
        title: "Création sur Framer",
        desc: "On construit votre site sur Framer : design ultra-moderne, chargement instantané, modifications faciles. Webflow disponible pour les projets sur-mesure plus complexes.",
    },
    {
        n: "04",
        icon: Rocket,
        title: "Lancement & suivi",
        desc: "On vous forme, on suit les résultats (nombre de visiteurs, d'appels, de devis), et on ajuste. On ne vous lâche pas après la livraison.",
    },
];

export default function Method({ compact = false }) {
    const containerRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start 0.6", "end 0.9"],
    });
    const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

    return (
        <section
            id="methode"
            ref={containerRef}
            className="relative bg-[#1A2F6E] text-white py-24 md:py-32 overflow-hidden"
            data-testid="method-section"
        >
<div
                aria-hidden
                className="absolute top-1/3 -right-32 w-[500px] h-[500px] rounded-full pointer-events-none"
                style={{ background: "radial-gradient(closest-side, rgba(43,123,228,0.2), transparent 70%)" }}
            />

            <div className="relative max-w-5xl mx-auto px-4 md:px-8">
                <div className="max-w-3xl">
                    <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-white/80">
                        Notre méthode
                    </span>
                    <h2
                        className="mt-6 font-display font-bold tracking-tight text-balance"
                        style={{ fontSize: "clamp(32px, 5vw, 48px)", lineHeight: 1.05 }}
                        data-testid="method-title"
                    >
                        4 étapes claires, zéro jargon.
                    </h2>
                    <p className="mt-6 text-lg text-white/70 leading-relaxed max-w-2xl">
                        Vous savez exactement ce qu'on fait, quand, et pourquoi. À chaque étape, vous validez avant qu'on avance.
                    </p>
                </div>

                <div className="relative mt-16 md:mt-20 pl-10 md:pl-16">
                    {/* Timeline track */}
                    <div className="absolute left-[18px] md:left-[28px] top-2 bottom-2 w-px bg-white/10">
                        <motion.div
                            style={{ height: lineHeight }}
                            className="w-full bg-gradient-to-b from-[#2B7BE4] to-[#2B7BE4] origin-top"
                            data-testid="method-progress-bar"
                        />
                    </div>

                    <ol className="space-y-14 md:space-y-20">
                        {STEPS.map((step, i) => (
                            <motion.li
                                key={step.n}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true, margin: "-100px" }}
                                transition={{ duration: 0.6, delay: i * 0.1 }}
                                className="relative"
                                data-testid={`method-step-${i}`}
                            >
                                {/* Dot */}
                                <div
                                    className="absolute -left-[38px] md:-left-[58px] top-1 flex items-center justify-center size-9 md:size-11 rounded-full bg-[#1A2F6E] border-2 border-[#2B7BE4] shadow-[0_0_0_6px_rgba(43,123,228,0.08)]"
                                    aria-hidden
                                >
                                    <step.icon size={16} className="text-[#2B7BE4]" />
                                </div>

                                <div className="flex items-baseline gap-4">
                                    <span className="font-display text-sm font-bold text-[#2B7BE4] tracking-widest">
                                        {step.n}
                                    </span>
                                    <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
                                        {step.title}
                                    </h3>
                                </div>
                                <p className={`mt-4 ${compact ? "text-base" : "text-base md:text-lg"} text-white/70 leading-relaxed max-w-2xl`}>
                                    {compact ? step.desc.split(".")[0] + "." : step.desc}
                                </p>
                                {step.cta && !compact && (
                                    <div className="mt-6">
                                        <PillButton
                                            as={Link}
                                            to="/audit-gratuit"
                                            variant="whitePill"
                                            size="md"
                                            data-testid="method-step-cta"
                                        >
                                            Réserver mon audit
                                            <ArrowRight size={16} />
                                        </PillButton>
                                    </div>
                                )}
                            </motion.li>
                        ))}
                    </ol>

                    {compact && (
                        <div className="mt-16 ml-[-1rem] md:ml-[-2rem]">
                            <PillButton
                                as={Link}
                                to="/methode"
                                variant="outlineLight"
                                size="md"
                                data-testid="method-see-full"
                            >
                                Voir la méthode complète
                                <ArrowRight size={16} />
                            </PillButton>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
